import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { SubmitButton } from "@/components/ui/button";
import {
  VillagePayloadSchema,
  useVillage,
  useVillageEdit,
  villagePayloadSchema,
} from "@/api/village";
import { VillageFormFields } from "./VillageFormFields";
import { Loading } from "@/components/ui/loading";
import { FormContainer } from "@/components/form/form-container";
import { Anchor } from "@/components/ui/anchor";
import { ErrorMessageFromResponse } from "@/api/types";

export function VillageEditForm({ id }: { id: string }) {
  const { data: village, isPending: isLoadingData } = useVillage({
    id,
  });
  const form = useForm<VillagePayloadSchema>({
    resolver: zodResolver(villagePayloadSchema),
    defaultValues: {
      villageCode: village?.villageCode,
      villageName: village?.villageName,
      subdistrict: village?.subdistrict,
    },
    values: village,
  });
  const { mutate, isPending } = useVillageEdit();
  const navigate = useNavigate();

  const handleSubmit = (values: VillagePayloadSchema) => {
    mutate(
      { id: id, data: values },
      {
        onSuccess: () => {
          swalSuccess
            .fire({
              text: "Sub District has been updated",
            })
            .then(() => navigate("/master-data/subdistrict"));
        },
        onError: (error) => {
          swalError.fire({
            text:
              (error as ErrorMessageFromResponse).response?.data.data ??
              error.message,
          });
        },
      },
    );
  };

  if (isLoadingData) {
    return <Loading />;
  }

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <VillageFormFields />
      <div className="ml-auto flex gap-x-2">
        <Anchor href="/master-data/subdistrict" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        <SubmitButton loading={isPending} className="ml-auto" />
      </div>
    </FormContainer>
  );
}
